import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Plus, Upload, Download, Trash2, Edit2, Search, Check, X,
  ChevronRight, FolderTree, Filter, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSectorsPage() {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [entries, setEntries] = useState([]);
  const [uniqueValues, setUniqueValues] = useState({ product_sectors: [], product_sub_sectors: [], categories: [], products: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterSector, setFilterSector] = useState('');
  const [filterSubSector, setFilterSubSector] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Add dialog
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ product_sector: '', product_sub_sector: '', category: '', product: '', grade: '' });

  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Upload
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('merge');
  const [uploading, setUploading] = useState(false);

  const fetchEntries = useCallback(async () => {
    const t = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!t) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSector) params.append('sector', filterSector);
      if (filterSubSector) params.append('sub_sector', filterSubSector);
      if (filterCategory) params.append('category', filterCategory);
      const res = await axios.get(`${API_URL}/api/admin/sectors?${params}`, { headers: { Authorization: `Bearer ${t}` } });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setUniqueValues(res.data.unique_values || {});
    } catch (err) {
      toast.error('Failed to load sectors');
    } finally {
      setLoading(false);
    }
  }, [filterSector, filterSubSector, filterCategory]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const filtered = entries.filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.product_sector || '').toLowerCase().includes(s) ||
      (e.product_sub_sector || '').toLowerCase().includes(s) ||
      (e.category || '').toLowerCase().includes(s) ||
      (e.product || '').toLowerCase().includes(s) ||
      (e.grade || '').toLowerCase().includes(s);
  });

  const handleAdd = async () => {
    if (!addForm.product_sector.trim()) { toast.error('Sector is required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/sectors`, addForm, { headers });
      toast.success('Sector entry added');
      setShowAdd(false);
      setAddForm({ product_sector: '', product_sub_sector: '', category: '', product: '', grade: '' });
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sector entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/sectors/${id}`, { headers });
      toast.success('Deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      product_sector: entry.product_sector,
      product_sub_sector: entry.product_sub_sector,
      category: entry.category,
      product: entry.product,
      grade: entry.grade,
    });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.patch(`${API_URL}/api/admin/sectors/${editingId}`, editForm, { headers });
      toast.success('Updated');
      setEditingId(null);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await axios.post(`${API_URL}/api/admin/sectors/upload-csv?mode=${uploadMode}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${res.data.added} entries added, ${res.data.skipped} skipped`);
      setShowUpload(false);
      setUploadFile(null);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/sectors/download-template`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sector_template.csv';
      a.click();
    } catch { toast.error('Download failed'); }
  };

  // Group entries by sector for the tree display
  const sectorGroups = {};
  filtered.forEach(e => {
    if (!sectorGroups[e.product_sector]) sectorGroups[e.product_sector] = [];
    sectorGroups[e.product_sector].push(e);
  });

  return (
    <div className="space-y-6" data-testid="admin-sectors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sector Classification</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the hierarchical product classification: Sector &gt; Sub-Sector &gt; Category &gt; Product &gt; Grade</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-1" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUpload(true)} data-testid="upload-csv-btn">
            <Upload className="h-4 w-4 mr-1" /> Upload CSV
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-sector-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Entry
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold">{uniqueValues.product_sectors?.length || 0}</div>
          <div className="text-xs text-gray-500">Sectors</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold">{uniqueValues.product_sub_sectors?.length || 0}</div>
          <div className="text-xs text-gray-500">Sub-Sectors</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold">{uniqueValues.categories?.length || 0}</div>
          <div className="text-xs text-gray-500">Categories</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold">{total}</div>
          <div className="text-xs text-gray-500">Total Entries</div>
        </CardContent></Card>
      </div>

      {/* Filters + Search */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-3 items-center flex-wrap">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={filterSector} onValueChange={v => { setFilterSector(v === '__all__' ? '' : v); setFilterSubSector(''); setFilterCategory(''); }}>
              <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All Sectors" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Sectors</SelectItem>
                {uniqueValues.product_sectors?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {filterSector && (
              <Select value={filterSubSector} onValueChange={v => { setFilterSubSector(v === '__all__' ? '' : v); setFilterCategory(''); }}>
                <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All Sub-Sectors" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Sub-Sectors</SelectItem>
                  {[...new Set(entries.filter(e => e.product_sector === filterSector).map(e => e.product_sub_sector).filter(Boolean))].sort().map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {filterSubSector && (
              <Select value={filterCategory} onValueChange={v => setFilterCategory(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Categories</SelectItem>
                  {[...new Set(entries.filter(e => e.product_sub_sector === filterSubSector).map(e => e.category).filter(Boolean))].sort().map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex-1" />
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
              <Input placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-7 h-8 w-56 text-xs" data-testid="sector-search" />
            </div>
            <Button variant="ghost" size="sm" onClick={fetchEntries}><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><FolderTree className="h-4 w-4" /> Classification Entries</CardTitle>
          <CardDescription>{filtered.length} entries shown</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Sector</TableHead>
                    <TableHead className="text-xs">Sub-Sector</TableHead>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs">Product</TableHead>
                    <TableHead className="text-xs">Grade</TableHead>
                    <TableHead className="text-xs w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(e => (
                    <TableRow key={e.id} className="text-sm hover:bg-gray-50">
                      {editingId === e.id ? (
                        <>
                          <TableCell><Input className="h-7 text-xs" value={editForm.product_sector} onChange={ev => setEditForm(p => ({...p, product_sector: ev.target.value}))} /></TableCell>
                          <TableCell><Input className="h-7 text-xs" value={editForm.product_sub_sector} onChange={ev => setEditForm(p => ({...p, product_sub_sector: ev.target.value}))} /></TableCell>
                          <TableCell><Input className="h-7 text-xs" value={editForm.category} onChange={ev => setEditForm(p => ({...p, category: ev.target.value}))} /></TableCell>
                          <TableCell><Input className="h-7 text-xs" value={editForm.product} onChange={ev => setEditForm(p => ({...p, product: ev.target.value}))} /></TableCell>
                          <TableCell><Input className="h-7 text-xs" value={editForm.grade} onChange={ev => setEditForm(p => ({...p, grade: ev.target.value}))} /></TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" className="h-6 px-2 text-xs bg-blue-600 text-white" onClick={handleSaveEdit}><Check className="h-3 w-3" /></Button>
                              <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setEditingId(null)}><X className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Badge className="bg-blue-50 text-blue-700 text-xs font-medium">{e.product_sector}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-gray-700">
                            {e.product_sub_sector && <span className="flex items-center gap-1"><ChevronRight className="h-3 w-3 text-gray-300" />{e.product_sub_sector}</span>}
                          </TableCell>
                          <TableCell className="text-xs text-gray-700">
                            {e.category && <span className="flex items-center gap-1"><ChevronRight className="h-3 w-3 text-gray-300" />{e.category}</span>}
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">{e.product}</TableCell>
                          <TableCell className="text-xs text-gray-500">{e.grade}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-500 hover:text-blue-600" onClick={() => startEdit(e)} data-testid={`edit-sector-${e.id}`}><Edit2 className="h-3 w-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-500 hover:text-red-600" onClick={() => handleDelete(e.id)} data-testid={`delete-sector-${e.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No entries found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-sector-dialog">
          <DialogHeader>
            <DialogTitle>Add Sector Entry</DialogTitle>
            <DialogDescription>Add a new classification to the hierarchy. Only Sector is required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Sector *</label>
              <Input value={addForm.product_sector} onChange={e => setAddForm(p => ({...p, product_sector: e.target.value}))} placeholder="e.g. Agriculture" data-testid="add-sector-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Sub-Sector</label>
              <Input value={addForm.product_sub_sector} onChange={e => setAddForm(p => ({...p, product_sub_sector: e.target.value}))} placeholder="e.g. Crops" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Category</label>
              <Input value={addForm.category} onChange={e => setAddForm(p => ({...p, category: e.target.value}))} placeholder="e.g. Cereals" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Product</label>
              <Input value={addForm.product} onChange={e => setAddForm(p => ({...p, product: e.target.value}))} placeholder="e.g. Maize" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Grade</label>
              <Input value={addForm.grade} onChange={e => setAddForm(p => ({...p, grade: e.target.value}))} placeholder="e.g. RB1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="add-sector-submit">Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent data-testid="upload-sector-dialog">
          <DialogHeader>
            <DialogTitle>Upload Sector CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV with columns: product_sector, product_sub_sector, category, product, grade
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600">Upload Mode</label>
              <Select value={uploadMode} onValueChange={setUploadMode}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge (add new, skip duplicates)</SelectItem>
                  <SelectItem value="replace">Replace (wipe existing, load fresh)</SelectItem>
                </SelectContent>
              </Select>
              {uploadMode === 'replace' && (
                <p className="text-xs text-red-500 mt-1">Warning: This will delete all existing sector entries and replace with the CSV data.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">CSV File</label>
              <Input type="file" accept=".csv" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="mt-1" data-testid="sector-csv-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleDownloadTemplate} size="sm"><Download className="h-3 w-3 mr-1" /> Download Template</Button>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading} data-testid="sector-upload-submit">
              {uploading ? <><RefreshCw className="h-4 w-4 animate-spin mr-1" /> Uploading...</> : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
